import { Level } from "../Level.js";
import { GRID_W, GRID_H } from "../../config/constants.js";
import { rndi, rnd, weightedPick } from "../../core/util.js";
import { scatterProps, finalize, walkableCells, gdist } from "./helpers.js";

/**
 * Graveyard: an open, foggy burial ground. Rows of gravestones, a few crypts,
 * scattered candles for eerie light, and a fenced perimeter. Built on open
 * ground (like the village) so paths between graves stay walkable; finalize()
 * guarantees a reachable exit.
 */
export function generateGraveyard(floor, theme) {
  const level = new Level(GRID_W, GRID_H, theme);
  const margin = 3;
  const x0 = margin, y0 = margin, x1 = GRID_W - margin, y1 = GRID_H - margin;

  for (let y = y0; y < y1; y++)
    for (let x = x0; x < x1; x++) level.setFloor(x, y, (x + y) % 2);

  const cx = Math.floor((x0 + x1) / 2), cy = Math.floor((y0 + y1) / 2);

  // a couple of crypts (large blocking structures), kept off the central path
  const crypts = 2 + Math.min(floor, 4);
  let placed = 0, tries = 0;
  while (placed < crypts && tries < 200) {
    tries++;
    const bx = rndi(x0 + 2, x1 - 4), by = rndi(y0 + 2, y1 - 4);
    if (gdist(bx, by, x0 + 1, cy) < 6) continue;
    const w = 2, h = 2;
    for (let yy = by; yy < by + h; yy++)
      for (let xx = bx; xx < bx + w; xx++) level.block[yy][xx] = 1;
    level.addProp({ gx: bx + w / 2, gy: by + h / 2, shape: "crypt", color: 0x6a6a6a,
      blocking: false, w, h, rot: (rndi(0, 3) * Math.PI) / 2, scale: 1,
      key: theme.assets && theme.assets.crypt ? theme.assets.crypt : null });
    placed++;
  }

  level.playerStart = { gx: x0 + 1, gy: cy };

  // gravestones in loose rows + scattered candles / crosses / coffins
  const open = walkableCells(level);
  const blockKinds = theme.props.filter((p) => p.blocking && p.weight > 0);
  for (const [gx, gy] of open) {
    if (gdist(gx, gy, x0 + 1, cy) < 5) continue;
    if (Math.random() < theme.propDensity) {
      const kind = weightedPick(blockKinds);
      level.addProp({ gx: gx + 0.5, gy: gy + 0.5, shape: kind.shape, color: kind.color,
        blocking: true, rot: rnd(-0.3, 0.3), scale: rnd(0.9, 1.15),
        key: theme.assets && theme.assets[kind.shape] ? theme.assets[kind.shape] : null });
    }
  }
  // soft props (candles / flowers) scattered for light and detail
  const softTheme = { ...theme, props: theme.props.filter((p) => !p.blocking) };
  scatterProps(level, softTheme, open, theme.propDensity * 0.6, [[x0 + 1, cy]]);

  for (const [gx, gy] of walkableCells(level))
    if (gdist(gx, gy, x0 + 1, cy) > 7) level.spawnPoints.push({ gx: gx + 0.5, gy: gy + 0.5 });

  return finalize(level);
}
